import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";
import {
  SignUpGoogleThunk,
  SignUpFacebookThunk,
  SignUpThunk,
  EmailConfirmationThunk,
  LoginThunk,
  ResendEmailThunk,
  GetSingpassAuthUrlThunk,
  SignUpSingpassThunk,
  SignUpVerificationSingpassThunk,
} from "./thunk";
import type {
  AuthState,
  EmailConfirmationResponse,
  FacebookSignupResponse,
  GoogleSignupResponse,
  LoginResponse,
  ResendEmailResponse,
  SignUpResponse,
  SingpassSignupResponse,
  SingpassVerification,
} from "../ts";
import {
  clearAppSession,
} from "../../common/redux/globalActions";
import { GetUserProfileThunk } from "../../account_settings/redux/thunk";

const initialState: AuthState = {
  is_authenticated: false,
  access: null,
  refresh: null,
  expires_at: null,
  code_phrase: null,
  user: null,
  login_method: null,
  singpass_verification: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    setAuthToken: (state, action) => {
      state.access = action.payload.access;
      if (action.payload.refresh) state.refresh = action.payload.refresh;
    },
    setAuthUser(state, action) {
      state.user = action.payload;
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Gloal Actions
      .addCase(clearAppSession, () => initialState)

      // Feature Actions
      .addMatcher(isFulfilled(SignUpThunk), (state, action) => {
        const payload = action.payload as SignUpResponse;
        state.expires_at = payload.data.expires_at ?? null;
        state.code_phrase = payload.data.code_phrase ?? null;
      })
      .addMatcher(isFulfilled(SignUpGoogleThunk), (state, action) => {
        const payload = action.payload as GoogleSignupResponse;
        state.access = payload.data.access;
        state.refresh = payload.data.refresh;
        state.singpass_verification = null;
        state.is_authenticated = !!(
          payload.data.access && payload.data.refresh
        );
      })
      .addMatcher(isFulfilled(SignUpFacebookThunk), (state, action) => {
        const payload = action.payload as FacebookSignupResponse;
        state.access = payload.data.access;
        state.refresh = payload.data.refresh;
        state.singpass_verification = null;
        state.is_authenticated = !!(
          payload.data.access && payload.data.refresh
        );
      })

      .addMatcher(isFulfilled(LoginThunk), (state, action) => {
        const payload = action.payload as LoginResponse;
        state.access = payload.data.access;
        state.refresh = payload.data.refresh;
        state.code_phrase = payload.data.code_phrase ?? null;
        state.expires_at = payload.data.expires_at ?? null;
        state.is_authenticated = !!(
          payload.data.access && payload.data.refresh
        );
      })

      .addMatcher(isFulfilled(SignUpSingpassThunk), (state, action) => {
        const payload = action.payload as SingpassSignupResponse;
        if (payload.data.access && payload.data.refresh) {
          state.access = payload.data.access;
          state.refresh = payload.data.refresh;
          state.is_authenticated = !!(
            payload.data.access && payload.data.refresh
          );
          state.singpass_verification = null;
        } else {
          state.singpass_verification = {
            ...(payload.data as SingpassVerification),
            code: action.meta.arg.code,
          };
          state.access = null;
          state.refresh = null;
        }
      })
      .addMatcher(isFulfilled(EmailConfirmationThunk), (state, action) => {
        const payload = action.payload as EmailConfirmationResponse;
        state.access = payload.data.access;
        state.refresh = payload.data.refresh;
        state.code_phrase = null;
        state.expires_at = null;
        state.is_authenticated = !!(
          payload.data.access && payload.data.refresh
        );
      })
      .addMatcher(isPending(SignUpGoogleThunk), (state) => {
        state.is_authenticated = false;
        state.login_method = "google";
      })
      .addMatcher(isPending(SignUpFacebookThunk), (state) => {
        state.is_authenticated = false;
        state.login_method = "facebook";
      })
      .addMatcher(
        isPending(SignUpThunk, LoginThunk, EmailConfirmationThunk),
        (state) => {
          state.is_authenticated = false;
          state.login_method = "email";
        },
      )
      .addMatcher(
        isPending(
          GetSingpassAuthUrlThunk,
          SignUpSingpassThunk,
          SignUpVerificationSingpassThunk,
        ),
        (state) => {
          state.is_authenticated = false;
          state.login_method = "singpass";
        },
      )
      .addMatcher(isRejected(SignUpVerificationSingpassThunk), (state) => {
        state.is_authenticated = false;
        state.login_method = null;
        state.access = null;
        state.refresh = null;
        state.code_phrase = null;
        state.expires_at = null;
      })
      .addMatcher(
        isRejected(
          SignUpGoogleThunk,
          SignUpFacebookThunk,
          SignUpThunk,
          EmailConfirmationThunk,
          LoginThunk,
          GetSingpassAuthUrlThunk,
          SignUpSingpassThunk,
          GetUserProfileThunk,
        ),
        (state) => {
          state.is_authenticated = false;
          state.login_method = null;
          state.singpass_verification = null;
          state.access = null;
          state.refresh = null;
          state.code_phrase = null;
          state.expires_at = null;
        },
      )
      .addMatcher(isFulfilled(ResendEmailThunk), (state, action) => {
        const payload = action.payload as ResendEmailResponse;
        state.expires_at = payload.data.expires_at;
        state.code_phrase = payload.data.code_phrase;
      });
  },
});

export const authActions = authSlice.actions;

export const { reducer: authReducer } = authSlice;

export default authReducer;
